import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Presence } from "@/components/Presence";
import { LanguagePill } from "@/components/session/FlowControls";
import { ArrowLeft, KeyRound, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useIsDesktop } from "@/hooks/useIsDesktop";

const PROMISES = [
  ["no name", "No account, no email, no name — you arrive and leave anonymous."],
  ["no trace", "Personal details are removed before anything is written down."],
  ["not forever", "Anything kept is deleted after ninety days."],
];

const LEAD = "I'm not here to tell you what to think. I'm here to ask the better question, and to notice what you might not have.";

const SPEAKERS = [["male", "a man's voice"], ["female", "a woman's voice"]];

/* Two voices sit in this room. Whoever you pick speaks for the whole conversation. */
const SpeakerPick = ({ value, onPick, accent }) => (
  <div
    data-testid="speaker-pick"
    className="flex gap-1 rounded-full border p-[3px]"
    style={{ borderColor: "var(--border-linen)", background: "rgba(244,239,230,0.02)" }}
  >
    {SPEAKERS.map(([k, label]) => (
      <button
        key={k}
        data-testid={`speaker-${k}`}
        aria-pressed={value === k}
        onClick={() => onPick(k)}
        className="flex-1 rounded-full py-2 font-mono text-[9.5px] uppercase tracking-[0.16em] transition-colors duration-300"
        style={value === k
          ? { background: `${accent}22`, color: accent }
          : { color: "var(--text-faint)" }}
      >
        {label}
      </button>
    ))}
  </div>
);

/* On a phone the three promises are one quiet line; the full wording is a tap away. */
const PromiseSheet = ({ open, onClose, accent }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end lg:hidden"
      data-testid="promises-sheet"
      onClick={onClose}
      style={{ background: "rgba(8,8,7,0.72)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="sheet-rise w-full rounded-t-[26px] border-t px-6 pb-9 pt-6"
        onClick={(e) => e.stopPropagation()}
        style={{ borderColor: "var(--border-linen)", background: "#141311" }}
      >
        <p className="eyebrow" style={{ color: accent }}>what happens here</p>
        <ul className="mt-4">
          {PROMISES.map(([tag, text]) => (
            <li key={tag} className="border-t py-3.5" style={{ borderColor: "var(--border-linen)" }}>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: accent }}>{tag}</p>
              <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--text-dim)" }}>{text}</p>
            </li>
          ))}
        </ul>
        <button
          data-testid="promises-close-btn"
          onClick={onClose}
          className="mt-5 w-full rounded-full border py-3 font-mono text-[10px] uppercase tracking-[0.24em]"
          style={{ borderColor: "var(--border-linen)", color: "var(--text-dim)" }}
        >
          understood
        </button>
      </div>
    </div>
  );
};

export default function Consent({ solo = false, slugOverride }) {
  const nav = useNavigate();
  const loc = useLocation();
  const params = useParams();
  const slug = slugOverride || params.slug;
  const isDesktop = useIsDesktop();
  const [lab, setLab] = useState(null);
  const [multi, setMulti] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [code, setCode] = useState(loc.state?.code || "");
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(false);
  const accent = lab?.accent || "#C87D55";

  useEffect(() => {
    api.status(slug).then(setLab).catch(() => nav("/", { replace: true }));
    if (solo) return;
    api.labs().then((rows) => setMulti(rows.length > 1)).catch(() => {});
  }, [slug, nav, solo]);

  const [pickedLang, setPickedLang] = useState(() => localStorage.getItem("griot-language") || "auto");
  const pickLang = (v) => { setPickedLang(v); localStorage.setItem("griot-language", v); };

  const [speaker, setSpeaker] = useState(() => localStorage.getItem("griot-speaker") || "male");
  const pickSpeaker = (v) => { setSpeaker(v); localStorage.setItem("griot-speaker", v); };

  const begin = async (typed = false) => {
    setBusy(true);
    try {
      const st = await api.status(slug);
      if (st.state !== "ok") return nav(`/fallback/${st.state}`);
      const s = await api.createSession(optIn, slug, code, speaker);
      nav("/session", { state: { sessionId: s.session_id, optIn, aiConfigured: st.ai_configured, labName: st.name, accent: st.accent, persona: st.persona, labSlug: slug, showTranscript: st.show_transcript, showGreeting: st.show_greeting, showNotes: st.show_notes !== false, noteMotion: st.note_motion !== false, showSources: false, sourcesOpen: false, allowText: st.allow_text !== false, language: pickedLang, speaker, text: typed, flow: !typed, realtime: st.realtime !== false } });
    } catch (e) {
      if (e?.response?.status === 403) return toast.error(errMsg(e));
      if (!e?.response) return nav("/fallback/offline");
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!lab) return <div className="min-h-screen" style={{ background: "var(--bg-primary)" }} data-testid="consent-loading" />;

  return (
    <div
      className="grain lamp relative overflow-hidden"
      style={{ minHeight: "100dvh", "--lab-accent": accent, background: "var(--bg-primary)" }}
      data-testid="consent-screen"
    >
      <div
        className="relative z-10 mx-auto flex w-full max-w-md flex-col overflow-hidden px-6 pb-4 pt-8 lg:!h-auto lg:max-w-6xl lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-24 lg:overflow-visible lg:px-14 lg:py-16"
        style={{ height: isDesktop ? "auto" : "100dvh" }}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain lg:flex-none lg:overflow-visible">
          {multi && (
            <button
              data-testid="change-lab-btn"
              onClick={() => nav("/")}
              className="mb-4 inline-flex w-fit items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.22em] transition-colors hover:text-[#F4EFE6] lg:mb-6"
              style={{ color: "var(--text-faint)" }}
            >
              <ArrowLeft size={12} /> all Labs
            </button>
          )}

          <div className="flex items-start gap-5">
            <span className="mt-1 h-14 w-[2px] shrink-0 rounded-full" style={{ background: `linear-gradient(180deg, transparent, ${accent}, transparent)` }} />
            <div>
              <p className="eyebrow rise" data-testid="consent-lab-name">{solo ? "Griot AI" : lab.name}</p>
              <h1 className="rise rise-1 mt-3 font-display text-[31px] font-light leading-[1.08] tracking-tight sm:text-[40px] lg:mt-4 lg:text-[58px]">
                I am Griot.
                <span className="hidden lg:inline">
                  <br />
                  <span style={{ color: "var(--text-dim)" }}>Storyteller, guide,<br />occasional trickster.</span>
                </span>
              </h1>
            </div>
          </div>

          <p className="rise rise-2 mt-4 max-w-[30rem] text-[13px] leading-[1.7] lg:ml-[25px] lg:mt-6 lg:text-[14px] lg:leading-[1.9]" style={{ color: "var(--text-dim)" }}>
            <span className="lg:hidden" style={{ color: "var(--text-main)" }}>Storyteller, guide, occasional trickster. </span>
            {LEAD}
            <span className="hidden lg:inline"> Everything I tell you about this Lab comes from what was actually recorded here — the questions are mine.</span>
          </p>

          {/* mobile: the three promises, held in one line */}
          <button
            data-testid="promises-line"
            onClick={() => setSheet(true)}
            className="rise rise-3 mt-5 flex w-fit items-center gap-2.5 border-t pt-4 font-mono text-[9.5px] uppercase tracking-[0.2em] lg:hidden"
            style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
          >
            <span style={{ color: accent }}>no name</span>
            <span className="opacity-40">·</span>
            <span style={{ color: accent }}>no trace</span>
            <span className="opacity-40">·</span>
            <span style={{ color: accent }}>90 days</span>
            <ChevronRight size={12} className="opacity-60" />
          </button>

          <ul className="rise rise-3 mt-5 hidden lg:ml-[25px] lg:mt-12 lg:block">
            {PROMISES.map(([tag, text]) => (
              <li key={tag} className="flex gap-4 border-t py-3 lg:gap-5 lg:py-4" style={{ borderColor: "var(--border-linen)" }}>
                <span className="mt-[3px] w-[62px] shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] lg:w-[70px]" style={{ color: accent }}>{tag}</span>
                <span className="text-[12.5px] leading-relaxed lg:text-[13px]" style={{ color: "var(--text-dim)" }}>{text}</span>
              </li>
            ))}
          </ul>

          {/* mobile: he is already breathing in the room you are about to walk into */}
          <div className="rise rise-4 pointer-events-none mt-auto flex min-h-0 flex-1 items-center justify-center py-3 lg:hidden">
            <Presence state="reflecting" accent={accent} height={210} />
          </div>
        </div>

        <div className="shrink-0 lg:flex lg:flex-col">
          {isDesktop && (
            <div className="rise rise-2 lg:-mr-10 lg:mb-10">
              <Presence state="reflecting" accent={accent} height={280} />
            </div>
          )}

          {lab.requires_code && (
            <div className="rise rise-3 mt-6 lg:mt-0">
              <label className="flex items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>
                <KeyRound size={12} style={{ color: accent }} /> this Lab asks for a code
              </label>
              <input
                data-testid="consent-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Lab code"
                className="mt-3 w-full rounded-2xl border bg-transparent px-4 py-3 font-mono text-[12px] uppercase tracking-[0.18em] placeholder:normal-case placeholder:tracking-normal focus:outline-none"
                style={{ borderColor: `${accent}55`, color: "var(--text-main)" }}
              />
            </div>
          )}

          {/* mobile: a slim row, not a card — it is a choice, not an announcement */}
          <label
            className="rise rise-4 mt-5 flex items-center justify-between gap-4 border-y py-3.5 lg:hidden"
            style={{ borderColor: "var(--border-linen)" }}
          >
            <span>
              <span className="block font-display text-[15px] font-light" style={{ color: "var(--text-main)" }}>Leave something behind</span>
              <span className="mt-0.5 block text-[11px] leading-snug" style={{ color: "var(--text-faint)" }}>Keep this talk, anonymised, so the Lab can learn from it</span>
            </span>
            <Switch data-testid="consent-capture-toggle" checked={optIn} onCheckedChange={setOptIn} style={optIn ? { backgroundColor: accent } : undefined} />
          </label>

          <div className="rise rise-4 mt-6 hidden items-center justify-between gap-5 rounded-[22px] p-5 lg:flex" style={{ border: "1px solid var(--border-linen)", background: "rgba(244,239,230,0.02)" }}>
            <div>
              <p className="font-display text-[19px] font-light" style={{ color: "var(--text-main)" }}>Leave something behind</p>
              <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--text-faint)" }}>
                Let this conversation be kept, anonymised, so the Lab can learn from it.
              </p>
            </div>
            <Switch data-testid="consent-capture-toggle-lg" checked={optIn} onCheckedChange={setOptIn} style={optIn ? { backgroundColor: accent } : undefined} />
          </div>

          <div
            className="sticky bottom-0 z-10 mt-auto pt-5 lg:static lg:mt-0 lg:pt-7"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)", background: "linear-gradient(180deg, transparent, var(--bg-primary) 38%)" }}
          >
            {lab.voice_choice !== false && (
              <div className="rise rise-4 mb-4">
                <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>
                  who sits with you
                </p>
                <SpeakerPick value={speaker} onPick={pickSpeaker} accent={accent} />
              </div>
            )}

            <button
              data-testid="consent-begin-btn"
              onClick={() => begin(false)}
              disabled={busy}
              className="w-full rounded-full py-3.5 font-mono text-[11px] uppercase tracking-[0.26em] transition-transform duration-500 active:scale-[0.985] disabled:opacity-60 lg:py-4"
              style={{ backgroundColor: accent, color: "#0C0C0B", boxShadow: `0 0 50px ${accent}4d` }}
            >
              {busy ? "opening" : "sit down with the Griot"}
            </button>

            {/* one row on a phone: the tongue he uses, and the way out of speaking altogether */}
            <div className="mt-3.5 flex items-center justify-between gap-3 lg:hidden">
              <LanguagePill lang={pickedLang} onLang={pickLang} accent={accent} />
              {lab.allow_text !== false ? (
                <button
                  data-testid="consent-text-btn"
                  onClick={() => begin(true)}
                  disabled={busy}
                  className="shrink-0 text-right font-mono text-[9.5px] uppercase leading-[1.5] tracking-[0.18em] transition-colors disabled:opacity-60"
                  style={{ color: "var(--text-faint)" }}
                >
                  rather type?<br />sit in silence
                </button>
              ) : (
                <span className="text-right font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: "var(--text-faint)" }}>
                  {pickedLang === "en" ? "english only" : pickedLang === "sw" ? "Kiswahili" : "Kiswahili, then english"}
                </span>
              )}
            </div>

            <div className="hidden lg:block">
              <p className="mt-4 text-center text-[11px]" style={{ color: "var(--text-faint)" }}>
                {solo ? "Just talk — I'll answer out loud." : "By continuing you accept the promises above."}
              </p>
              <div className="mt-4 flex flex-col items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>
                  {pickedLang === "en" ? "he will speak english only" : pickedLang === "sw" ? "atazungumza Kiswahili" : "he opens in Kiswahili, then English"}
                </span>
                <LanguagePill lang={pickedLang} onLang={pickLang} accent={accent} />
              </div>
              {lab.allow_text !== false && (
                <button
                  data-testid="consent-text-btn-lg"
                  onClick={() => begin(true)}
                  disabled={busy}
                  className="mx-auto mt-3 block font-mono text-[10px] uppercase tracking-[0.2em] transition-colors disabled:opacity-60"
                  style={{ color: "color-mix(in srgb, var(--text-faint) 85%, transparent)" }}
                >
                  rather type? sit down in silence
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <PromiseSheet open={sheet} onClose={() => setSheet(false)} accent={accent} />
    </div>
  );
}
